import {
  FormSchema,
  FormData,
  isInputElement,
  isFileInputElement,
  FormHelper,
  isMyNumberInputElement,
  IFormSchemaV2,
  IFormSchema,
  isInputClassElement,
} from "@graffer-inc/form-schema";
import { createInitialCloudGovSchema } from "./applicant-schema";
import {
  ApplicantTypeLiteralList,
  ApplicantTypeLiterals,
} from "../../application/applicant";
import { ReservedApplicantIdHelper } from "./applicant-schema/applicant-reserved-ids";
import * as lodash from "lodash";
import { CloudGovFormSchemaAccessor } from "./smart-apply-form-schema-validator/accessor-helper";
import { ApplicantSchemaKeys } from "./applicant-schema-keys";

/**
 * フォームの認証タイプ。
 *
 * jpki: マイナンバーカードの電子署名
 * moj: 商業登記の電子証明書による署名
 * none: 署名不要
 */
export type FormAuthType = "jpki" | "moj" | "none";

/**
 * CloudGovにおけるFormSchema定義、以下の理由から特別なSectionElementを保持している。
 *
 * CloudGovにおいては、申請者に関する固定の情報があり、それを検索・識別・連絡（メールなどの通知）の用途に使うためにシステム的に認識できるようにする必要がある。
 * 同時に、申請フローの中においては、申請者情報に基づいてフォームの内容が動的に変わるなどを可能にする必要がある。
 * そのため、SectionElementに事前に予約された申請者のセクション (applicant-schema)を持つようにしており、このクラスではその制約を満たしているか、の一貫性チェックを行う。
 * see: https://github.com/graffer-inc/cloud-gov/blob/develop/backend/src/domain/applications/applicant.go
 */
export class CloudGovFormSchema extends FormSchema {
  constructor(values: IFormSchemaV2) {
    super(values);
  }

  hasApplicantEmail(applicantSchemaKeys: ApplicantSchemaKeys) {
    const applicantEmailElement = this.accessorHelper(
      applicantSchemaKeys,
    ).getApplicantElement(applicantSchemaKeys.applicantEmail.id);
    if (!applicantEmailElement || !isInputElement(applicantEmailElement)) {
      return false;
    }
    // FIXME: https://docs.google.com/document/d/10zcmpPNH-5H13oeJTDDeIQSjd4LOPd39-jpU5HyYnCM/edit?ts=5fd1993e
    //        この問題の暫定対応
    const isAllHidden = !this.supportedApplicantTypes(applicantSchemaKeys).some(
      (tp) => {
        const applyingRule = this.accessorHelper(
          applicantSchemaKeys,
        ).getApplyingRuleFor(tp, applicantSchemaKeys.applicantEmail.id);
        // 条件付きルールがなかったり、hiddenではないものが設定されている場合は、いずれかが表示になっていると判定
        return !applyingRule || !applyingRule.rule.hidden;
      },
    );
    return !isAllHidden;
  }

  hasMyNumberInput() {
    return !!this.flattenDeep()
      .filter(isInputClassElement)
      .find(isMyNumberInputElement);
  }

  nextSectionIndex(
    sectIndex: number,
    formData: FormData,
    formAuthType: FormAuthType,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ): number | null {
    const nextSect = sectIndex + 1;
    if (nextSect > this.root.length - 1) {
      return null;
    }
    if (
      !this.checkAllHidden(
        nextSect,
        formData,
        formAuthType,
        applicantSchemaKeys,
      )
    ) {
      return nextSect;
    }
    return this.nextSectionIndex(
      nextSect,
      formData,
      formAuthType,
      applicantSchemaKeys,
    );
  }

  prevSectionIndex(
    sectIndex: number,
    formData: FormData,
    formAuthType: FormAuthType,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ): number | null {
    const prevSect = sectIndex - 1;
    if (prevSect < 0) {
      return null;
    }
    if (
      !this.checkAllHidden(
        prevSect,
        formData,
        formAuthType,
        applicantSchemaKeys,
      )
    ) {
      return prevSect;
    }
    return this.prevSectionIndex(
      prevSect,
      formData,
      formAuthType,
      applicantSchemaKeys,
    );
  }

  applicantSection(applicantSchemaKeys: ApplicantSchemaKeys) {
    return this.accessorHelper(applicantSchemaKeys).applicantSection!;
  }

  /**
   * このFormSchemaがサポートしている申請者の種別の一覧を取得する
   */
  private supportedApplicantTypes(
    applicantSchemaKeys: ApplicantSchemaKeys,
  ): ApplicantTypeLiterals[] {
    return this.accessorHelper(applicantSchemaKeys).supportedApplicantTypes;
  }

  /**
   * formDataのrefKeyを初期化する。本来はform-schemaに実装するが、暫定的にここに実装する。
   */
  clearRefKeys(formData: FormData, refKeys: string[]): FormData {
    const newData = JSON.parse(JSON.stringify(formData));
    this.flattenDeep()
      .filter(isInputClassElement)
      .forEach((elem) => {
        if (isFileInputElement(elem) || isMyNumberInputElement(elem)) {
          if (
            refKeys.indexOf(
              <string>FormHelper.getValue(newData, elem.formFieldId),
            ) !== -1
          ) {
            lodash.unset(newData, elem.formFieldId);
            if (!!lodash.get(newData, elem.formFieldId)) {
              throw new Error(`failed to unset path value`);
            }
          }
        }
      });
    return newData;
  }

  clearRefKeysExcludes(formData: FormData, excludes: string[]): FormData {
    const newData = JSON.parse(JSON.stringify(formData));
    this.flattenDeep()
      .filter(isInputClassElement)
      .forEach((elem) => {
        if (isFileInputElement(elem) || isMyNumberInputElement(elem)) {
          if (
            excludes.indexOf(
              <string>FormHelper.getValue(newData, elem.formFieldId),
            ) === -1
          ) {
            lodash.unset(newData, elem.formFieldId);
            if (!!lodash.get(newData, elem.formFieldId)) {
              throw new Error(`failed to unset path value`);
            }
          }
        }
      });
    return newData;
  }

  applicantSectionConditionalRules(applicantSchemaKeys: ApplicantSchemaKeys) {
    return this.accessorHelper(
      applicantSchemaKeys,
    ).applicantSectionConditionalRules();
  }

  userAreaConditionalRule(applicantSchemaKeys: ApplicantSchemaKeys) {
    return this.accessorHelper(applicantSchemaKeys).userAreaConditionalRule;
  }

  /**
   * ユーザーが設定した条件付きルールの最後のIndex. 設定がない場合は、-1を返す
   */
  userAreaLastIndex(applicantSchemaKeys: ApplicantSchemaKeys) {
    return this.accessorHelper(applicantSchemaKeys).userAreaLastIndex();
  }

  /**
   * 指定のIndexが申請者セクションのための条件付きルールかどうかをチェックする
   */
  isApplicantSectionConditionalRules(
    index: number,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) {
    return this.userAreaLastIndex(applicantSchemaKeys) < index;
  }

  /**
   * ２つのFormDataを比べて、参照キーに関する修正が入っていないかチェックする
   */
  checkRefKeyValueNotChanged(prev: FormData, next: FormData) {
    return (
      this.flattenDeep()
        .filter(
          (elem) =>
            isInputClassElement(elem) &&
            (isMyNumberInputElement(elem) || isFileInputElement(elem)),
        )
        .filter((v) => {
          return (
            FormHelper.getValue(prev, v.formFieldId) !==
            FormHelper.getValue(next, v.formFieldId)
          );
        }).length === 0
    );
  }

  private checkAllHidden(
    sectIndex: number,
    formData: FormData,
    formAuthType: FormAuthType,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) {
    const notHiddenExist = !!this.root[sectIndex]
      .applyRules(this.filterApplyingRule(formData))
      .flatten()
      .find((e) => {
        if (
          formAuthType === "jpki" &&
          (ReservedApplicantIdHelper.isContainMyNumber(
            e.id,
            applicantSchemaKeys,
          ) ||
            ReservedApplicantIdHelper.isApplicantType(
              e.id,
              applicantSchemaKeys,
            ))
        ) {
          return false;
        }

        if (
          formAuthType === "moj" &&
          (ReservedApplicantIdHelper.isContainMoj(e.id, applicantSchemaKeys) ||
            ReservedApplicantIdHelper.isApplicantType(
              e.id,
              applicantSchemaKeys,
            ) ||
            ReservedApplicantIdHelper.isApplicantCorporateSearch(
              e.id,
              applicantSchemaKeys,
            ))
        ) {
          return false;
        }
        return !e.hidden;
      });
    return !notHiddenExist;
  }

  copy() {
    return new CloudGovFormSchema(JSON.parse(JSON.stringify(this)));
  }

  static initial(supported?: ApplicantTypeLiterals[]): CloudGovFormSchema {
    let s = supported || ApplicantTypeLiteralList;
    return new CloudGovFormSchema(
      FormSchema.fromJSON(createInitialCloudGovSchema(s)),
    );
  }

  static fromJSON(sc: IFormSchema): CloudGovFormSchema {
    return new CloudGovFormSchema(FormSchema.fromJSON(sc));
  }

  private accessorHelper(
    applicantSchemaKeys: ApplicantSchemaKeys,
  ): CloudGovFormSchemaAccessor {
    return new CloudGovFormSchemaAccessor(this, applicantSchemaKeys);
  }

  /**
   * @deprecated
   * use `buildInitialDocument` instead
   * ApplicantSchemaKeys が必要なため、引数を受け取ることができる buildInitialDocument を使用すること。
   * FormSchema を継承しているため、このメソッドは、引数がないインターフェースでアクセスできるようにしておく必要がある。
   */
  public toInitialDocument() {
    // 動作を担保しつつも、呼び出しがあった場合に
    // application で設定している Sentry から検知できるよう、
    // Promise の rejection を行なっておく。
    new Promise(() => {
      throw new Error(
        "toInitialDocument is deprecated. use `buildInitialDocument` instead",
      );
    });

    return super.toInitialDocument();
  }

  private safeSuperInitialDocument() {
    return super.toInitialDocument();
  }

  static buildInitialDocument(
    instance: CloudGovFormSchema,
    applicantSchemaKeys: ApplicantSchemaKeys,
  ) {
    const initialDocs = instance.safeSuperInitialDocument();
    if (instance.supportedApplicantTypes(applicantSchemaKeys).length === 1) {
      return {
        ...initialDocs,
        [applicantSchemaKeys.applicantType.id]:
          instance.supportedApplicantTypes(applicantSchemaKeys)[0],
      };
    }
    return initialDocs;
  }
}
